import React, { useEffect, useState } from 'react'
import {
  Card, CardBody, Col, Form, Input, Label, Row, Spinner,
} from 'reactstrap'
import QdContainer from 'components/Common/QdContainer'
import { useLocation, useNavigate } from 'react-router-dom'
import { post } from 'utils/apiClient'

const ORANGE = '#EE5A29'
const GREEN = '#1a7a55'
const RED = '#c0392b'

function GmailAuth() {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [callback, setCallback] = useState(null)
  const [overrideFor, setOverrideFor] = useState(null)

  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const status = params.get('status')
    if (!status) return

    setCallback({
      status,
      email: params.get('email') || '',
      message: params.get('message') || '',
    })
    if (params.get('email')) setEmail(params.get('email'))

    navigate(location.pathname, { replace: true })
  }, [])

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

  const startAuth = async ({ targetEmail, force }) => {
    setError(null)
    setSubmitting(true)
    try {
      const response = await post('oauth/gmail/start', {
        email: targetEmail,
        redirect_to: window.location.origin + '/onboarding/gmail-auth',
        force: !!force,
      })
      if (response?.already_connected) {
        setOverrideFor(response.email || targetEmail)
        setSubmitting(false)
        return
      }
      if (!response?.auth_url) {
        throw new Error(response?.error || 'Failed to start authorization')
      }
      openOAuthPopup(response.auth_url)
    } catch (err) {
      setError(err.message || 'Failed to start authorization')
      setSubmitting(false)
    }
  }

  const openOAuthPopup = (authUrl) => {
    const width = 600
    const height = 700
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2
    const popup = window.open(
      authUrl,
      'gmail-oauth',
      `width=${width},height=${height},left=${left},top=${top}`
    )

    if (!popup || popup.closed) {
      setError('Popup was blocked. Please allow popups for this site and try again.')
      setSubmitting(false)
      return
    }

    let settled = false

    const messageHandler = (event) => {
      if (event.data?.type !== 'gmail-oauth-result') return
      settled = true
      window.removeEventListener('message', messageHandler)
      clearInterval(closedTimer)
      setCallback({
        status: event.data.status,
        email: event.data.email || '',
        message: event.data.message || '',
      })
      setOverrideFor(null)
      setSubmitting(false)
      try { popup.close() } catch (e) {}
    }
    window.addEventListener('message', messageHandler)

    const closedTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(closedTimer)
        if (!settled) {
          window.removeEventListener('message', messageHandler)
          setSubmitting(false)
        }
      }
    }, 500)
  }

  const handleConnect = async (e) => {
    e.preventDefault()
    setError(null)
    setOverrideFor(null)

    if (!isValidEmail(email)) {
      setError('Please enter a valid Gmail address.')
      return
    }
    await startAuth({ targetEmail: email.trim().toLowerCase(), force: false })
  }

  const handleConfirmOverride = async () => {
    await startAuth({ targetEmail: overrideFor, force: true })
  }

  const handleCancelOverride = () => {
    setOverrideFor(null)
  }

  const handleReconnect = () => {
    setCallback(null)
    setError(null)
  }

  const headingStyle = {
    fontSize: '14px',
    fontWeight: 600,
    borderBottom: `2px solid ${ORANGE}`,
    paddingBottom: '6px',
    display: 'inline-block',
    marginBottom: '20px',
    color: ORANGE,
  }

  const isSuccess = callback?.status === 'success'

  return (
    <QdContainer>
      <Row>
        <Col lg={7}>
          <Card className="p-3 shadow-sm">
            <CardBody className="p-0">
              <h6 style={headingStyle}>Gmail Authorization</h6>

              <p className="text-muted mb-4" style={{ fontSize: '13px' }}>
                Connect a tenant Gmail account to QUED. You will be redirected to Google to sign in
                and grant access. Once approved, the account will be connected.
              </p>

              <Form onSubmit={handleConnect} className="p-2">
                <Row className="mb-4">
                  <Label className="col-sm-3 col-form-label">Gmail Address</Label>
                  <Col sm={7}>
                    <Input
                      type="email"
                      className="form-control"
                      name="email"
                      placeholder="tenant@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={submitting}
                      required
                    />
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col sm={{ offset: 3 }}>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={submitting || !email}
                    >
                      {submitting ? (
                        <>
                          <Spinner size="sm" /> &nbsp;Redirecting…
                        </>
                      ) : (
                        <>
                          <i className="bx bxl-google me-1" /> Connect with Google
                        </>
                      )}
                    </button>
                    {callback && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm ms-2"
                        onClick={handleReconnect}
                        disabled={submitting}
                      >
                        Reconnect a different account
                      </button>
                    )}
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {error && (
        <Row className="mt-3">
          <Col lg={7}>
            <div className="alert alert-danger" style={{ fontSize: '13px' }}>{error}</div>
          </Col>
        </Row>
      )}

      {overrideFor && (
        <Row className="mt-3">
          <Col lg={7}>
            <Card className="shadow-sm" style={{ borderLeft: `4px solid ${ORANGE}` }}>
              <CardBody className="p-3" style={{ fontSize: '13px' }}>
                <div style={{ fontWeight: 600, marginBottom: '6px' }}>Already connected</div>
                <div className="text-muted" style={{ marginBottom: '12px' }}>
                  <strong>{overrideFor}</strong> is already connected to QUED. Re-authorizing will
                  replace the existing access with a fresh one. Continue?
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleConfirmOverride}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" /> &nbsp;Redirecting…
                    </>
                  ) : (
                    'Yes, re-authorize'
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm ms-2"
                  onClick={handleCancelOverride}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {callback && (
        <Row className="mt-3">
          <Col lg={7}>
            <Card className="shadow-sm">
              <CardBody className="p-3">
                <h6 style={headingStyle}>Result</h6>
                <div
                  className="d-flex align-items-center"
                  style={{ fontSize: '13px', color: isSuccess ? GREEN : RED }}
                >
                  <i
                    className={isSuccess ? 'bx bx-check-circle me-2' : 'bx bx-x-circle me-2'}
                    style={{ fontSize: '20px' }}
                  />
                  <div>
                    {isSuccess ? (
                      <>
                        <div style={{ fontWeight: 600 }}>Connected successfully</div>
                        <div className="text-muted" style={{ fontSize: '12px' }}>
                          {callback.email
                            ? `${callback.email} is now connected to QUED.`
                            : 'The Gmail account is now connected to QUED.'}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontWeight: 600 }}>Connection failed</div>
                        <div className="text-muted" style={{ fontSize: '12px' }}>
                          {callback.message || 'Google did not approve the connection. Please try again.'}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </QdContainer>
  )
}

export default GmailAuth
